import { useCallback, useEffect, useMemo, useState } from 'react';
import { PageHeader } from '@/deploy/components/layout/PageHeader';
import { Button } from '@/deploy/components/ui/Button';
import { Badge } from '@/deploy/components/ui/Badge';
import { Icon } from '@/deploy/components/ui/Icon';
import { Modal } from '@/deploy/components/ui/Modal';
import { PermissionLock } from '@/deploy/components/ui/PermissionLock';
import { JobModal } from '@/deploy/components/network/JobModal';
import { CertificateCreateModal, CertificateSettingsModal, StepsModal } from '@/deploy/components/network/CertificateModals';
import { CERT_STATUS, errorMessage, expiryBadge, formatDate } from '@/deploy/components/network/networkFormat';
import styles from '@/deploy/components/network/Network.module.scss';
import { networkService } from '@/deploy/services/network.service';
import { useSessionStore } from '@/deploy/stores/session.store';
import { can } from '@/deploy/utils/permissions';
import type { Certificate, CertificateKind, NetworkJob, ProjectDomain, TargetSummary } from '@/deploy/types/network';

type Dialog =
  | { kind: 'create' }
  | { kind: 'settings' | 'steps' | 'delete'; cert: Certificate }
  | { kind: 'job'; job: NetworkJob; title: string };

const TABS: { id: CertificateKind; label: string; description: string }[] = [
  { id: 'INTERNAL', label: 'Reverse proxy', description: 'Servis par nginx, rechargé après chaque renouvellement.' },
  { id: 'EXTERNAL', label: 'Externes', description: 'Déployés par SSH sur un serveur tiers après chaque renouvellement.' },
];

/** Réseau > Certificats SSL : certificats du projet actif, internes et externes. */
export function NetworkCertificates() {
  const project = useSessionStore((s) => s.currentProject());
  const user = useSessionStore((s) => s.user);
  const canEdit = can(user, 'cicd.network');

  const [tab, setTab] = useState<CertificateKind>('INTERNAL');
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [targets, setTargets] = useState<TargetSummary[]>([]);
  const [projectDomains, setProjectDomains] = useState<ProjectDomain[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<Dialog | null>(null);

  const load = useCallback(async () => {
    if (!project.id) return;
    setError(null);
    try {
      const [c, t, d] = await Promise.all([
        networkService.listCertificates(project.id),
        networkService.targets(project.id),
        networkService.domains(project.id),
      ]);
      setCertificates(c);
      setTargets(t);
      setProjectDomains(d.domains);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  const visible = useMemo(() => certificates.filter((c) => c.kind === tab), [certificates, tab]);
  const counts = useMemo(() => ({
    INTERNAL: certificates.filter((c) => c.kind === 'INTERNAL').length,
    EXTERNAL: certificates.filter((c) => c.kind === 'EXTERNAL').length,
  }), [certificates]);
  const fetchJob = useCallback((id: number) => networkService.job(project.id, id), [project.id]);

  const runJob = async (cert: Certificate, action: 'renew' | 'deploy') => {
    try {
      const job = action === 'renew'
        ? await networkService.renewCertificate(project.id, cert.id)
        : await networkService.deployCertificate(project.id, cert.id);
      setDialog({ kind: 'job', job, title: `${action === 'renew' ? 'Renouvellement' : 'Déploiement'} : ${cert.name}` });
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  const defaultRenewBefore = certificates.find((c) => c.renewBeforeDays === null)?.effectiveRenewBeforeDays;

  return (
    <div className={styles.page}>
      <PageHeader
        eyebrow="Réseau"
        title="Certificats SSL"
        subtitle={`Certificats Let's Encrypt de ${project.name}, renouvelés automatiquement.`}
        actions={
          <Button variant="primary" leftIcon={<Icon name="plus" size={14} />} onClick={() => setDialog({ kind: 'create' })}
            disabled={!canEdit || projectDomains.length === 0}
            title={!canEdit ? 'Permission cicd.network requise' : undefined}>
            Nouveau certificat
            {!canEdit && <PermissionLock inline />}
          </Button>
        }
      />

      {error && <div className={styles.error}>{error}</div>}

      <nav className={styles.tabs}>
        {TABS.map((t) => (
          <button key={t.id} type="button" className={[styles.tab, tab === t.id && styles.tabActive].filter(Boolean).join(' ')}
            onClick={() => setTab(t.id)}>
            {t.label} <Badge tone="muted">{counts[t.id]}</Badge>
          </button>
        ))}
      </nav>
      <div className={styles.toolbar}>
        <span className={styles.muted}>{TABS.find((t) => t.id === tab)!.description}</span>
        <span className={styles.spacer} />
        <Button variant="ghost" size="sm" leftIcon={<Icon name="refresh" size={12} />} onClick={() => void load()}>Rafraîchir</Button>
      </div>

      {loading ? (
        <div className={styles.empty}>Chargement…</div>
      ) : visible.length === 0 ? (
        <div className={styles.empty}>Aucun certificat {tab === 'INTERNAL' ? 'de reverse proxy' : 'externe'} pour ce projet.</div>
      ) : (
        <div className={styles.list}>
          <div className={styles.rowHead}>
            <span>Certificat</span>
            <span>Etat</span>
            <span>Expiration</span>
            <span />
          </div>
          {visible.map((c) => {
            const status = CERT_STATUS[c.status];
            const expiry = expiryBadge(c.notAfter);
            return (
              <div key={c.id} className={styles.row}>
                <div className={styles.cell}>
                  <span className={styles.primary}>{c.name}</span>
                  <span className={styles.mono} title={c.domains.join(', ')}>{c.domains.join(', ')}</span>
                  <div className={styles.cellInline}>
                    <Badge tone="muted">{c.challenge === 'DNS01' ? 'DNS' : 'HTTP'}</Badge>
                    <Badge tone="muted">{c.keyType}</Badge>
                    {c.imported && <Badge tone="warning">Importé</Badge>}
                  </div>
                </div>
                <div className={styles.cell}>
                  <div className={styles.cellInline}>
                    <Badge tone={status.tone} dot>{status.label}</Badge>
                    {c.failureCount > 0 && <Badge tone="danger">{c.failureCount} échec(s)</Badge>}
                  </div>
                  {c.lastError && <span className={styles.muted} title={c.lastError}>{c.lastError.slice(0, 120)}</span>}
                  {c.nextAttemptAt && <span className={styles.muted}>Nouvel essai : {formatDate(c.nextAttemptAt)}</span>}
                </div>
                <div className={styles.cell}>
                  <div className={styles.cellInline}>
                    <Badge tone={expiry.tone} dot>{expiry.label}</Badge>
                    <span className={styles.muted}>{formatDate(c.notAfter)}</span>
                  </div>
                  <span className={styles.muted}>
                    Renouvellement {c.effectiveRenewBeforeDays} j avant{c.renewBeforeDays === null ? ' (défaut)' : ''}
                    {c.renewalDate ? ` : ${formatDate(c.renewalDate)}` : ''}
                  </span>
                </div>
                <div className={styles.actions}>
                  <Button variant="softPrimary" size="sm" leftIcon={<Icon name="refresh" size={12} />} disabled={!canEdit}
                    onClick={() => void runJob(c, 'renew')}>
                    Renouveler
                  </Button>
                  {c.kind === 'EXTERNAL' && (
                    <>
                      <Button variant="ghost" size="sm" leftIcon={<Icon name="layers" size={12} />}
                        onClick={() => setDialog({ kind: 'steps', cert: c })}>
                        Procédure
                      </Button>
                      <Button variant="ghost" size="sm" leftIcon={<Icon name="deploy" size={12} />} disabled={!canEdit}
                        onClick={() => void runJob(c, 'deploy')}>
                        Déployer
                      </Button>
                    </>
                  )}
                  <Button variant="ghost" size="sm" leftIcon={<Icon name="settings" size={12} />} disabled={!canEdit}
                    onClick={() => setDialog({ kind: 'settings', cert: c })}>
                    Réglages
                  </Button>
                  <Button variant="softDanger" size="sm" leftIcon={<Icon name="x" size={12} />} disabled={!canEdit}
                    onClick={() => setDialog({ kind: 'delete', cert: c })}>
                    Supprimer
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {dialog?.kind === 'create' && (
        <CertificateCreateModal projectId={project.id} kind={tab === 'INTERNAL' ? 'INTERNAL' : 'EXTERNAL'}
          projectDomains={projectDomains} targets={targets} defaultRenewBefore={defaultRenewBefore}
          onClose={() => setDialog(null)} onJob={(job, title) => setDialog({ kind: 'job', job, title })} />
      )}
      {dialog?.kind === 'settings' && (
        <CertificateSettingsModal projectId={project.id} certificate={dialog.cert} onClose={() => setDialog(null)}
          onSaved={() => { setDialog(null); void load(); }} />
      )}
      {dialog?.kind === 'steps' && (
        <StepsModal projectId={project.id} certificate={dialog.cert} targets={targets} onClose={() => setDialog(null)}
          onJob={(job, title) => setDialog({ kind: 'job', job, title })} />
      )}
      {dialog?.kind === 'delete' && (
        <DeleteCertificateModal projectId={project.id} certificate={dialog.cert} onClose={() => setDialog(null)}
          onDeleted={() => { setDialog(null); void load(); }} />
      )}
      {dialog?.kind === 'job' && (
        <JobModal job={dialog.job} title={dialog.title} fetchJob={fetchJob} onClose={() => setDialog(null)} onDone={() => void load()} />
      )}
    </div>
  );
}

function DeleteCertificateModal({ projectId, certificate, onClose, onDeleted }: {
  projectId: string;
  certificate: Certificate;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await networkService.deleteCertificate(projectId, certificate.id);
      onDeleted();
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="Supprimer le certificat" subtitle={certificate.name} width="sm"
      footer={<>
        <Button variant="ghost" onClick={onClose} disabled={busy}>Annuler</Button>
        <Button variant="danger" onClick={submit} loading={busy}>Supprimer</Button>
      </>}>
      <div className={styles.form}>
        <span>
          Le certificat n'est plus suivi ni renouvelé.
          {certificate.kind === 'EXTERNAL' ? ' Les fichiers déjà déployés sur les serveurs restent en place.' : ' Refusé tant qu\'un domaine l\'utilise.'}
        </span>
        {error && <div className={styles.error}>{error}</div>}
      </div>
    </Modal>
  );
}
